import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChildren,
  QueryList,
  AfterViewInit,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PriceHistoryComponent } from '../../../../shared/components/price-history/price-history.component';
import { Price } from '../../../../shared/models/catalog/price.model';
import {
  Product,
  StoreProductSchema,
} from '../../../../shared/models/catalog/product.model';
import { Ticket } from '../../../../shared/models/catalog/ticket.model';
import { AdditionalProductService } from '../../../../shared/services/catalog/additional-product.service';
import { CategoryService } from '../../../../shared/services/catalog/category.service';
import { OpeningHourTypeService } from '../../../../shared/services/catalog/opening-hour-type.service';
import { ProductService } from '../../../../shared/services/catalog/product.service';
import { HasUnsavedChanges } from '../../../../shared/guards/unsaved-changes.guard';
import {
  DEFAULT_LANGUAGE_CODE,
  LANGUAGE_CODE_LABELS,
  LANGUAGE_CODES,
  LanguageCode,
} from '../../../../shared/util/language-codes';
import { applyZodErrors } from '../../../../shared/util/zod-form-errors';
import { TicketPanelComponent } from './ticket-panel/ticket-panel.component';

interface PanelEntry {
  localId: number;
  ticket: Ticket | null;
}

interface PersistedProductDetail {
  language: string;
  title: string;
  description_short?: string | null;
  description_long?: string | null;
}

interface LanguageOption {
  label: string;
  value: LanguageCode;
}

@Component({
  selector: 'app-product-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    MessageModule,
    MultiSelectModule,
    SelectModule,
    TextareaModule,
    ToggleSwitchModule,
    TagModule,
    ToastModule,
    ConfirmDialogModule,
    PriceHistoryComponent,
    TicketPanelComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './product-detail.component.html',
  styleUrl: './product-detail.component.scss',
})
export class ProductDetailComponent
  implements OnInit, AfterViewInit, HasUnsavedChanges
{
  productService = inject(ProductService);
  categoryService = inject(CategoryService);
  openingHourTypeService = inject(OpeningHourTypeService);
  additionalProductService = inject(AdditionalProductService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #fb = inject(FormBuilder);

  @ViewChildren(TicketPanelComponent)
  ticketPanelRefs!: QueryList<TicketPanelComponent>;

  model = signal<Product | null>(null);
  isEditMode = signal(false);
  loading = signal(false);
  submitting = signal(false);

  panels = signal<PanelEntry[]>([]);
  #nextLocalId = 1;

  productPrices = computed<Price[]>(() => {
    const m = this.model();
    return Array.isArray(m?.prices) ? (m.prices as Price[]) : [];
  });

  hasDirtyPanels = signal(false);

  // Triggers re-evaluation of usedLanguages/availableLanguages when the
  // FormArray changes shape. Bumped on add/remove.
  detailsRevision = signal(0);

  defaultLanguage = DEFAULT_LANGUAGE_CODE;

  form: FormGroup = this.#fb.group({
    details: this.#fb.array<FormGroup>([]),
    key: ['', [Validators.required, Validators.maxLength(255)]],
    category_id: [null as number | null, [Validators.required]],
    opening_hour_type_id: [null as number | null, [Validators.required]],
    duration: [null as number | null],
    interval: [null as number | null],
    for_request: [false as boolean],
    active: [true as boolean],
    notes: [null as string | null],
    additional_product_ids: [[] as number[]],
  });

  languagePickerControl = new FormControl<LanguageCode | null>(null);

  get details(): FormArray<FormGroup> {
    return this.form.get('details') as FormArray<FormGroup>;
  }

  usedLanguages = computed<Set<LanguageCode>>(() => {
    this.detailsRevision();
    const used = new Set<LanguageCode>();
    for (const ctrl of this.details.controls) {
      const lang = ctrl.get('language')?.value as LanguageCode | undefined;
      if (lang) used.add(lang);
    }
    return used;
  });

  availableLanguages = computed<LanguageOption[]>(() => {
    const used = this.usedLanguages();
    return LANGUAGE_CODES.filter((code) => !used.has(code)).map((code) => ({
      label: LANGUAGE_CODE_LABELS[code],
      value: code,
    }));
  });

  ngOnInit(): void {
    this.categoryService.getCategories();
    this.openingHourTypeService.getAll();
    this.additionalProductService.getAll();
    const idParam = this.#route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'new') {
      this.isEditMode.set(true);
      this.#loadModel(Number(idParam));
    } else {
      // New product: seed a single empty DEU row.
      this.#resetDetailsTo([]);
    }
  }

  ngAfterViewInit(): void {
    this.ticketPanelRefs.changes.subscribe(() => this.recomputeDirty());
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty || this.hasDirtyPanels();
  }

  recomputeDirty() {
    if (!this.ticketPanelRefs) {
      this.hasDirtyPanels.set(false);
      return;
    }
    const dirty = this.ticketPanelRefs.toArray().some((p) => p.isDirty());
    this.hasDirtyPanels.set(dirty);
  }

  productSaveDisabled(): boolean {
    return this.submitting() || !this.form.valid || this.hasDirtyPanels();
  }

  dirtyPanelTitles(): string[] {
    if (!this.ticketPanelRefs) return [];
    return this.ticketPanelRefs
      .toArray()
      .filter((p) => p.isDirty())
      .map((p) => p.headerTitle());
  }

  formatDisplayPrice(): string {
    const m = this.model();
    if (!m || m.display_price === null || m.display_price === undefined)
      return '—';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(m.display_price);
  }

  labelForLanguage(code: string): string {
    return LANGUAGE_CODE_LABELS[code as LanguageCode] ?? code;
  }

  addLanguageRow() {
    const lang = this.languagePickerControl.value;
    if (!lang) return;
    if (this.usedLanguages().has(lang)) return;
    this.details.push(this.#buildDetailGroup(lang));
    this.languagePickerControl.setValue(null);
    this.detailsRevision.update((n) => n + 1);
    this.form.markAsDirty();
  }

  hasPendingLanguage(): boolean {
    return this.languagePickerControl.value !== null;
  }

  removeLanguageRow(index: number) {
    const ctrl = this.details.at(index);
    if (!ctrl) return;
    if ((ctrl.get('language')?.value as LanguageCode) === DEFAULT_LANGUAGE_CODE) {
      return;
    }
    this.details.removeAt(index);
    this.detailsRevision.update((n) => n + 1);
    this.form.markAsDirty();
  }

  #loadModel(id: number) {
    this.loading.set(true);
    this.productService.getById(id).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.model.set(response.data);
        this.#patchFromProduct(response.data);
        this.#syncPanelsFromProduct(response.data);
        this.form.markAsPristine();
        setTimeout(() => this.recomputeDirty(), 0);
      },
      error: (e: { status?: number }) => {
        this.loading.set(false);
        if (e?.status === 404) {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant('catalog.products.notFound'),
          });
          this.#router.navigate(['/catalog/products']);
        } else {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant('catalog.products.loadError'),
          });
        }
      },
    });
  }

  #buildDetailGroup(language: LanguageCode, seed?: PersistedProductDetail): FormGroup {
    return this.#fb.group({
      language: [language, [Validators.required]],
      title: [
        seed?.title ?? '',
        [Validators.required, Validators.maxLength(255)],
      ],
      description_short: [seed?.description_short ?? null as string | null],
      description_long: [seed?.description_long ?? null as string | null],
    });
  }

  #resetDetailsTo(rows: PersistedProductDetail[]) {
    while (this.details.length > 0) {
      this.details.removeAt(0);
    }

    // DEU first, then any other persisted rows (only the curated set; unknown
    // codes from older seeds are dropped). DEU row is always present, even
    // when persisted rows don't include it (new products, or pre-migration
    // legacy data).
    const byLang = new Map<LanguageCode, PersistedProductDetail>();
    for (const row of rows) {
      if ((LANGUAGE_CODES as readonly string[]).includes(row.language)) {
        byLang.set(row.language as LanguageCode, row);
      }
    }
    this.details.push(
      this.#buildDetailGroup(DEFAULT_LANGUAGE_CODE, byLang.get(DEFAULT_LANGUAGE_CODE)),
    );
    for (const code of LANGUAGE_CODES) {
      if (code === DEFAULT_LANGUAGE_CODE) continue;
      const seed = byLang.get(code);
      if (seed) {
        this.details.push(this.#buildDetailGroup(code, seed));
      }
    }
    this.detailsRevision.update((n) => n + 1);
  }

  #patchFromProduct(product: Product) {
    const persisted = (
      product as unknown as { details?: PersistedProductDetail[] }
    ).details ?? [];

    const linkedAddonIds = ((product as unknown as { additional_products?: Array<{ id: number }> }).additional_products ?? []).map((a) => a.id);
    this.form.reset({
      details: [],
      key: product.key,
      category_id: product.category_id,
      opening_hour_type_id: product.opening_hour_type_id,
      duration: product.duration,
      interval: product.interval,
      for_request: product.for_request ?? false,
      active: product.active,
      notes:
        ((product as unknown as { notes?: string | null }).notes) ?? null,
      additional_product_ids: linkedAddonIds,
    });

    this.#resetDetailsTo(persisted);
  }

  #syncPanelsFromProduct(product: Product) {
    const tickets = (product.tickets as Ticket[]) ?? [];
    this.panels.set(
      tickets.map((t) => ({ localId: this.#nextLocalId++, ticket: t })),
    );
  }

  addTicket() {
    if (!this.model()) return;
    this.panels.update((arr) => [
      ...arr,
      { localId: this.#nextLocalId++, ticket: null },
    ]);
    setTimeout(() => this.recomputeDirty(), 0);
  }

  onTicketSaved(panel: PanelEntry, ticket: Ticket) {
    panel.ticket = ticket;
    this.panels.update((arr) => [...arr]);
    this.reload();
  }

  onTicketDeleted(panel: PanelEntry) {
    this.panels.update((arr) => arr.filter((p) => p.localId !== panel.localId));
    if (panel.ticket) {
      this.reload();
    }
    setTimeout(() => this.recomputeDirty(), 0);
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.hasDirtyPanels()) {
      this.#messageService.add({
        severity: 'warn',
        summary: this.#translate.instant('catalog.tickets.dirtyHint'),
        detail: this.dirtyPanelTitles().join(', '),
      });
      return;
    }

    const raw = this.form.getRawValue();
    const payload = {
      category_id: raw.category_id,
      opening_hour_type_id: raw.opening_hour_type_id,
      key: raw.key,
      duration: raw.duration,
      interval: raw.interval,
      for_request: raw.for_request,
      active: raw.active,
      notes: raw.notes,
      additional_product_ids: Array.isArray(raw.additional_product_ids)
        ? (raw.additional_product_ids as number[])
        : [],
      details: (raw.details as PersistedProductDetail[]).map((row) => ({
        language: row.language as LanguageCode,
        title: row.title,
        description_short: row.description_short ?? null,
        description_long: row.description_long ?? null,
      })),
    };

    const parsed = StoreProductSchema.safeParse(payload);
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    this.submitting.set(true);
    const persisted = this.model();
    const call$ = persisted
      ? this.productService.update(persisted.id, parsed.data)
      : this.productService.create(parsed.data);

    call$.subscribe({
      next: (response) => {
        this.submitting.set(false);
        this.model.set(response.data);
        this.form.markAsPristine();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            persisted
              ? 'catalog.products.updated'
              : 'catalog.products.created',
          ),
        });
        if (!persisted) {
          this.isEditMode.set(true);
          this.#router.navigate(['/catalog/products', response.data.id]);
        } else {
          this.#syncPanelsFromProduct(response.data);
        }
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        this.#applyServerErrors(e);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            persisted
              ? 'catalog.products.updateError'
              : 'catalog.products.createError',
          ),
        });
      },
    });
  }

  onDelete(event: Event) {
    const item = this.model();
    if (!item) return;
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant('catalog.products.deleteConfirmHeader'),
      message: this.#translate.instant('catalog.products.deleteConfirmMessage'),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.productService.delete(item.id).subscribe({
          next: () => {
            this.form.markAsPristine();
            this.panels.set([]);
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant('catalog.products.deleted'),
            });
            this.#router.navigate(['/catalog/products']);
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translate.instant('label.errorHeader'),
              detail: this.#translate.instant('catalog.products.deleteError'),
            });
          },
        });
      },
    });
  }

  onCancel() {
    this.#router.navigate(['/catalog/products']);
  }

  reload() {
    const item = this.model();
    if (item) this.#loadModel(item.id);
  }

  onPanelDirtyChange() {
    this.recomputeDirty();
  }

  /**
   * Map server-side `details.{n}.{field}` paths to the corresponding
   * FormArray control path so error messages land on the right control.
   */
  #applyServerErrors(error: unknown) {
    const errBag = (error as { error?: { errors?: Record<string, string[]> } })
      ?.error?.errors;
    if (!errBag) return;
    for (const key of Object.keys(errBag)) {
      const target = key.replace(
        /^details\.(\d+)\.(.+)$/,
        'details.$1.$2',
      );
      const control = this.form.get(target);
      if (control) {
        control.setErrors({ server: { message: errBag[key][0] } });
        control.markAsTouched();
      }
    }
  }
}
